import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Text,
  Badge,
  Alert,
  AlertIcon,
  useToast,
  useColorModeValue,
  Icon,
  Divider,
  Switch,
  FormControl,
  FormLabel,
  Heading,
  Spinner,
  Code,
  Link,
  Center,
  SimpleGrid,
  Tooltip,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiCheckCircle,
  FiXCircle,
  FiRefreshCw,
  FiExternalLink,
  FiMail,
  FiZap,
  FiClock,
  FiAlertTriangle,
  FiUploadCloud,
} from 'react-icons/fi';
import { app } from '../../../utils/init-firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';

function timeAgo(iso) {
  if (!iso) return 'never';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'never';
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export default function CalendarSyncSettings({ userId }) {
  const toast = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncingNow, setIsSyncingNow] = useState(false);
  const [isTogglingPause, setIsTogglingPause] = useState(false);
  const [isTestingSignature, setIsTestingSignature] = useState(false);
  const [signatureResult, setSignatureResult] = useState(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const infoBG = useColorModeValue('blue.50', 'blue.900');
  const statBg = useColorModeValue('gray.50', 'gray.700');

  const fetchStatus = useCallback(async () => {
    try {
      const functions = getFunctions(app, 'us-east1');
      const getStatus = httpsCallable(functions, 'getCalendarSyncStatus');
      const result = await getStatus({});
      setStatus(result.data);
    } catch (error) {
      console.error('[CalendarSyncSettings] Failed to load sync status:', error);
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchStatus();
  }, [userId, fetchStatus]);

  const isConnected = status?.connected === true;
  const needsReconnect = status?.needsReconnect === true;
  const syncPaused = status?.syncPaused === true;
  const watchLive = status?.watch?.status === 'active';

  // Post-OAuth initial import. Runs in the background AFTER the connect
  // popup has already closed, so big calendars can't time out the connect UX.
  // If the user navigates away mid-import, the 15-minute scheduler finishes
  // the job -- this just makes the first import immediate.
  const runBootstrapImport = async () => {
    try {
      const functions = getFunctions(app, 'us-east1');
      const syncNow = httpsCallable(functions, 'syncCalendarNow', { timeout: 540000 });
      const result = await syncNow({ bootstrap: true });

      const imported = result.data?.inbound?.created || 0;
      toast({
        title: 'Google Calendar import complete',
        description: `${imported} event${imported === 1 ? '' : 's'} imported from Google`,
        status: 'success',
        duration: 4000,
      });
    } catch (error) {
      // Not fatal: the connection is established; the scheduler will finish
      // the import within 15 minutes.
      console.warn('[CalendarSyncSettings] bootstrap import did not finish in-page:', error);
    } finally {
      fetchStatus();
    }
  };

  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const initOAuth = httpsCallable(functions, 'initCalendarOAuth');

      const result = await initOAuth({
        redirectUri: `${window.location.origin}/oauth/calendar/callback`,
      });

      if (result.data.authUrl) {
        const messageHandler = (event) => {
          if (event.origin !== window.location.origin) return;

          if (event.data.type === 'oauth_success') {
            setIsConnecting(false);
            toast({
              title: 'Calendar connected!',
              description: 'Two-way sync is active. Importing your Google events in the background…',
              status: 'success',
              duration: 4000,
            });
            window.removeEventListener('message', messageHandler);
            fetchStatus();
            runBootstrapImport();
          } else if (event.data.type === 'oauth_error') {
            setIsConnecting(false);

            const errorMsg = event.data.error || 'Could not connect';
            const isTokenError = errorMsg.includes('refresh token') || errorMsg.includes('failed-precondition');

            toast({
              title: 'Connection failed',
              description: isTokenError
                ? 'Google did not return authorization. Revoke access at myaccount.google.com/permissions, then reconnect.'
                : errorMsg,
              status: 'error',
              duration: 7000,
              isClosable: true,
            });
            window.removeEventListener('message', messageHandler);
          }
        };

        window.addEventListener('message', messageHandler);

        const width = 600;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        window.open(
          result.data.authUrl,
          'GoogleCalendarOAuth',
          `width=${width},height=${height},left=${left},top=${top},noopener=no`
        );

        // Watchdog: give up listening after 5 minutes (slow consent screens,
        // abandoned popups). Success/error handlers remove the listener sooner.
        setTimeout(() => {
          window.removeEventListener('message', messageHandler);
          setIsConnecting(false);
        }, 300000);
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);

      const errorMsg = error.message || 'Could not connect to Google Calendar';
      const isTokenError = errorMsg.includes('refresh token') || errorMsg.includes('failed-precondition');

      toast({
        title: 'Connection failed',
        description: isTokenError
          ? 'Google did not provide authorization. Please revoke access at https://myaccount.google.com/permissions and try again.'
          : errorMsg,
        status: 'error',
        duration: 7000,
        isClosable: true,
      });
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm(
      'Disconnect Google Calendar? This stops all syncing and unlinks your Google account from BOTH Origin CRM and EOS.'
    )) {
      return;
    }

    setIsDisconnecting(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const disconnectCalendar = httpsCallable(functions, 'disconnectCalendar');

      await disconnectCalendar({});

      toast({
        title: 'Calendar disconnected',
        description: 'Google Calendar has been unlinked from Origin and EOS',
        status: 'success',
        duration: 3000,
      });
      fetchStatus();
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      toast({
        title: 'Error disconnecting',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleSyncNow = async () => {
    setIsSyncingNow(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const syncNow = httpsCallable(functions, 'syncCalendarNow');
      const result = await syncNow({});

      const inbound = result.data?.inbound;
      const outbound = result.data?.outbound;
      if (result.data?.success) {
        const pulled = (inbound?.created || 0) + (inbound?.updated || 0);
        toast({
          title: 'Sync complete',
          description: `Pulled ${pulled} change${pulled === 1 ? '' : 's'} from Google, pushed ${outbound?.processed || 0} to Google`,
          status: 'success',
          duration: 4000,
        });
      } else {
        toast({
          title: 'Sync had problems',
          description: result.data?.inboundError || 'Check the connection status below',
          status: 'warning',
          duration: 5000,
        });
      }
      fetchStatus();
    } catch (error) {
      console.error('Error running manual sync:', error);
      toast({
        title: 'Sync failed',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSyncingNow(false);
    }
  };

  const handleTogglePause = async (paused) => {
    setIsTogglingPause(true);
    try {
      const functions = getFunctions(app, 'us-east1');
      const setPaused = httpsCallable(functions, 'setCalendarSyncPaused');
      await setPaused({ paused });

      toast({
        title: paused ? 'Sync paused' : 'Sync resumed',
        description: paused
          ? 'Nothing will sync in either direction until you resume'
          : 'Catching up on changes now',
        status: paused ? 'info' : 'success',
        duration: 3000,
      });
      fetchStatus();
    } catch (error) {
      console.error('Error toggling pause:', error);
      toast({
        title: 'Error updating setting',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsTogglingPause(false);
    }
  };

  const handleTestSignature = async () => {
    setIsTestingSignature(true);
    setSignatureResult(null);

    try {
      const functions = getFunctions(app, 'us-east1');
      const fetchSignature = httpsCallable(functions, 'fetchUserSignature');

      const result = await fetchSignature({});

      if (result.data.success) {
        setSignatureResult(result.data);

        if (result.data.hasSignature) {
          toast({
            title: 'Signature found!',
            description: `Successfully retrieved your Gmail signature (${result.data.signature.length} characters)`,
            status: 'success',
            duration: 4000,
          });
        } else {
          toast({
            title: 'No signature configured',
            description: 'No Gmail signature found. Configure one in Gmail settings.',
            status: 'info',
            duration: 4000,
          });
        }
      }
    } catch (error) {
      console.error('Error testing signature:', error);

      toast({
        title: 'Error fetching signature',
        description: error.message || 'Check console for details',
        status: 'error',
        duration: 5000,
      });

      setSignatureResult({ error: error.message });
    } finally {
      setIsTestingSignature(false);
    }
  };

  if (loading) {
    return (
      <Center py={8}>
        <Spinner />
      </Center>
    );
  }

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="md" mb={2}>Google Calendar Integration</Heading>
        <Text fontSize="sm" color="gray.600">
          True 2-way sync: meetings, events, and tasks flow between Origin and Google Calendar automatically in both directions.
        </Text>
      </Box>

      <Divider />

      {/* Reconnect banner */}
      {needsReconnect && (
        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Google Calendar needs to be reconnected
            </Text>
            <Text fontSize="xs">
              Your Google authorization expired or was revoked, so syncing is stopped. Reconnect to resume.
            </Text>
          </VStack>
          <Button
            size="sm"
            colorScheme="orange"
            onClick={handleConnect}
            isLoading={isConnecting}
            loadingText="Connecting..."
          >
            Reconnect
          </Button>
        </Alert>
      )}

      {/* Connection Status */}
      <Box p={5} bg={infoBG} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
        <HStack justify="space-between" mb={4}>
          <HStack spacing={3}>
            <Icon as={FiCalendar} boxSize={6} color={isConnected ? 'green.500' : 'gray.400'} />
            <VStack align="flex-start" spacing={0}>
              <Text fontWeight="semibold">Connection Status</Text>
              {isConnected && status?.connectedAt && (
                <Text fontSize="xs" color="gray.600">
                  Connected: {new Date(status.connectedAt).toLocaleString()}
                </Text>
              )}
            </VStack>
          </HStack>

          {isConnected ? (
            <Badge colorScheme={syncPaused ? 'yellow' : 'green'} fontSize="sm" px={3} py={1}>
              <HStack spacing={1}>
                <Icon as={syncPaused ? FiClock : FiCheckCircle} boxSize={3} />
                <Text>{syncPaused ? 'Paused' : 'Connected'}</Text>
              </HStack>
            </Badge>
          ) : (
            <Badge colorScheme={needsReconnect ? 'orange' : 'gray'} fontSize="sm" px={3} py={1}>
              <HStack spacing={1}>
                <Icon as={FiXCircle} boxSize={3} />
                <Text>{needsReconnect ? 'Reconnect Required' : 'Not Connected'}</Text>
              </HStack>
            </Badge>
          )}
        </HStack>

        {isConnected ? (
          <HStack spacing={2} flexWrap="wrap">
            <Button
              size="sm"
              leftIcon={<FiZap />}
              colorScheme="blue"
              onClick={handleSyncNow}
              isLoading={isSyncingNow}
              loadingText="Syncing..."
            >
              Sync Now
            </Button>
            <Button
              size="sm"
              leftIcon={<FiRefreshCw />}
              variant="outline"
              onClick={handleConnect}
              isLoading={isConnecting}
              loadingText="Connecting..."
            >
              Refresh Connection
            </Button>
            <Button
              size="sm"
              leftIcon={<FiXCircle />}
              colorScheme="red"
              variant="outline"
              onClick={handleDisconnect}
              isLoading={isDisconnecting}
              loadingText="Disconnecting..."
            >
              Disconnect
            </Button>
          </HStack>
        ) : (
          !needsReconnect && (
            <Button
              colorScheme="blue"
              leftIcon={<FiCalendar />}
              onClick={handleConnect}
              isLoading={isConnecting}
              loadingText="Connecting..."
            >
              Connect Google Calendar
            </Button>
          )
        )}
      </Box>

      {/* Sync health dashboard */}
      {isConnected && (
        <>
          <Box>
            <Heading size="sm" mb={4}>Sync Health</Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              <Tooltip label="Google pushes changes to Origin instantly while a live channel is active. Without one, changes are picked up by the 15-minute fallback poll.">
                <Box p={3} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <HStack spacing={1} mb={1}>
                    <Icon as={FiZap} boxSize={3} color={watchLive ? 'green.500' : 'gray.400'} />
                    <Text fontSize="xs" color="gray.500">Real-time updates</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold">
                    {watchLive ? 'Live' : 'Polling (15 min)'}
                  </Text>
                </Box>
              </Tooltip>

              <Tooltip label="The last time changes were pulled in from Google Calendar.">
                <Box p={3} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <HStack spacing={1} mb={1}>
                    <Icon as={FiRefreshCw} boxSize={3} color="blue.400" />
                    <Text fontSize="xs" color="gray.500">Last pull from Google</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold">
                    {timeAgo(status?.lastInboundSyncAt)}
                  </Text>
                </Box>
              </Tooltip>

              <Tooltip label="Changes made in Origin that are waiting to be pushed to Google. Normally 0 — items clear within seconds.">
                <Box p={3} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <HStack spacing={1} mb={1}>
                    <Icon as={FiUploadCloud} boxSize={3} color={status?.pendingOutbox > 0 ? 'orange.400' : 'green.500'} />
                    <Text fontSize="xs" color="gray.500">Waiting to push</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold">
                    {status?.pendingOutbox ?? 0} item{status?.pendingOutbox === 1 ? '' : 's'}
                  </Text>
                </Box>
              </Tooltip>

              <Tooltip label="Health of your Google authorization token.">
                <Box p={3} bg={statBg} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
                  <HStack spacing={1} mb={1}>
                    <Icon
                      as={status?.tokenStatus === 'healthy' ? FiCheckCircle : FiAlertTriangle}
                      boxSize={3}
                      color={status?.tokenStatus === 'healthy' ? 'green.500' : 'orange.400'}
                    />
                    <Text fontSize="xs" color="gray.500">Authorization</Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="semibold" textTransform="capitalize">
                    {status?.tokenStatus || 'Unknown'}
                  </Text>
                </Box>
              </Tooltip>
            </SimpleGrid>

            {status?.lastError && (
              <Alert status="warning" borderRadius="md" mt={3} fontSize="sm">
                <AlertIcon />
                <VStack align="flex-start" spacing={0} flex={1}>
                  <Text fontSize="xs" fontWeight="semibold">Last sync error</Text>
                  <Code fontSize="xs" bg="transparent" p={0} whiteSpace="pre-wrap">
                    {status.lastError}
                  </Code>
                </VStack>
              </Alert>
            )}
          </Box>

          {/* Pause switch */}
          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box flex={1} mr={4}>
              <FormLabel fontSize="sm" mb={1} fontWeight="semibold">
                Pause Syncing
              </FormLabel>
              <Text fontSize="xs" color="gray.600">
                Temporarily stop syncing in both directions. Nothing is deleted — changes catch up when you resume.
              </Text>
            </Box>
            <Switch
              isChecked={syncPaused}
              onChange={(e) => handleTogglePause(e.target.checked)}
              isDisabled={isTogglingPause}
              colorScheme="blue"
              size="lg"
            />
          </FormControl>
        </>
      )}

      {!isConnected && !needsReconnect && (
        <Alert status="info" size="sm" borderRadius="md">
          <AlertIcon />
          <VStack align="flex-start" spacing={1} flex={1}>
            <Text fontSize="sm" fontWeight="semibold">
              Connect Google Calendar
            </Text>
            <Text fontSize="xs">
              Meetings and events you create in Origin appear on your Google Calendar within seconds (with invites and Meet links),
              tasks with due dates show up as calendar items, and anything you change in Google Calendar flows back into Origin automatically.
            </Text>
          </VStack>
        </Alert>
      )}

      {/* Gmail Signature Test */}
      <Box>
        <Heading size="sm" mb={4}>Gmail Signature Test</Heading>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Test fetching your Gmail signature to ensure the integration is working correctly.
        </Text>

        <VStack align="stretch" spacing={3}>
          <Button
            leftIcon={<FiMail />}
            colorScheme="purple"
            variant="outline"
            onClick={handleTestSignature}
            isLoading={isTestingSignature}
            loadingText="Fetching signature..."
            size="sm"
          >
            Test Fetch My Signature
          </Button>

          {signatureResult && (
            <Box
              p={4}
              bg={signatureResult.error ? 'red.50' : 'green.50'}
              borderRadius="md"
              borderWidth="1px"
              borderColor={signatureResult.error ? 'red.200' : 'green.200'}
            >
              {signatureResult.error ? (
                <VStack align="flex-start" spacing={2}>
                  <HStack>
                    <Icon as={FiXCircle} color="red.500" />
                    <Text fontWeight="semibold" fontSize="sm" color="red.700">
                      Error
                    </Text>
                  </HStack>
                  <Code fontSize="xs" p={2} bg="white" borderRadius="md" w="full">
                    {signatureResult.error}
                  </Code>
                </VStack>
              ) : (
                <VStack align="flex-start" spacing={2}>
                  <HStack>
                    <Icon as={FiCheckCircle} color="green.500" />
                    <Text fontWeight="semibold" fontSize="sm" color="green.700">
                      {signatureResult.hasSignature ? 'Signature Found' : 'No Signature'}
                    </Text>
                  </HStack>
                  {signatureResult.hasSignature && (
                    <>
                      <Text fontSize="xs" color="gray.600">
                        Length: {signatureResult.signature.length} characters
                      </Text>
                      <Box
                        maxH="200px"
                        overflowY="auto"
                        p={3}
                        bg="white"
                        borderRadius="md"
                        borderWidth="1px"
                        w="full"
                      >
                        <Box dangerouslySetInnerHTML={{ __html: signatureResult.signature }} />
                      </Box>
                    </>
                  )}
                </VStack>
              )}
            </Box>
          )}
        </VStack>
      </Box>

      <Divider />

      {/* Help Text */}
      <Box fontSize="xs" color="gray.500" p={4} bg={bgColor} borderRadius="md" borderWidth="1px" borderColor={borderColor}>
        <Text fontWeight="semibold" mb={2}>How syncing works</Text>
        <VStack align="flex-start" spacing={1}>
          <Text>
            • Origin → Google: new meetings, events, and dated tasks push to Google Calendar within seconds
          </Text>
          <Text>
            • Google → Origin: changes arrive instantly via live notifications, with a 15-minute fallback poll as backup
          </Text>
          <Text>
            • Completing or deleting a task removes its calendar item from Google automatically
          </Text>
          <Text>
            • If connection fails after disconnecting, revoke access in your{' '}
            <Link
              href="https://myaccount.google.com/permissions"
              isExternal
              color="blue.500"
              textDecoration="underline"
            >
              Google Account settings <Icon as={FiExternalLink} boxSize={2} />
            </Link>
            {' '}first, then reconnect.
          </Text>
          <Text>
            • Gmail signature is fetched automatically when sending emails through CRM
          </Text>
        </VStack>
      </Box>
    </VStack>
  );
}
